export type FairyId = string;

export const FAIRY_SPAWN_MIN_SEC = 3;
export const FAIRY_SPAWN_MAX_SEC = 5;
export const FAIRY_MAX_ACTIVE = 2;
export const FAIRY_HOVER_POP_SEC = 2;
export const FAIRY_POP_FAI_REWARD = 1;

const DEFAULT_FAIRY_BOUNDS = {
  minX: -0.68,
  maxX: 0.54,
  minY: -0.42,
  maxY: 0.34,
} as const;

export const FAIRY_SIZE_MIN = 18;
export const FAIRY_SIZE_MAX = 28;
export const FAIRY_GLOW_SCALE = 2.2;

const FAIRY_MOVE_SPEED = 0.08;

export const FAIRY_VIBRATION_AMPLITUDE = 2;
export const FAIRY_VIBRATION_FREQUENCY = 6;

const FAIRY_COLOR_SATURATION = 0.45;
const FAIRY_COLOR_VALUE = 1;
const FAIRY_COLOR_ALPHA = 0.95;
const FAIRY_GLOW_ALPHA = 0.25;

export interface FairyColorSnapshot {
  readonly hue: number;
  readonly saturation: number;
  readonly value: number;
  readonly alpha: number;
}

export interface FairyEntitySnapshot {
  readonly id: FairyId;

  readonly x: number;
  readonly y: number;

  readonly vibrationOffsetX: number;
  readonly vibrationOffsetY: number;

  readonly size: number;
  readonly glowSize: number;
  readonly color: FairyColorSnapshot;
  readonly glowAlpha: number;

  readonly hoverProgress: number;
  readonly isHovered: boolean;
}

export interface FairySnapshot {
  readonly fairies: readonly FairyEntitySnapshot[];
}

export interface FairyTickResult {
  readonly popped: readonly FairyId[];
}

export interface FairyBounds {
  readonly minX: number;
  readonly maxX: number;
  readonly minY: number;
  readonly maxY: number;
}

interface FairyEntity {
  id: FairyId;
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  hue: number;
  vibrationPhase: number;
  hoverElapsed: number;
  isHovered: boolean;
}

export interface FairyModelOptions {
  readonly rng?: () => number;
}

export class FairyModel {
  private readonly rng: () => number;
  private fairies: FairyEntity[] = [];
  private bounds: FairyBounds = DEFAULT_FAIRY_BOUNDS;
  private spawnEnabled = false;
  private spawnTimer: number;
  private nextFairyId = 1;

  constructor(options: FairyModelOptions = {}) {
    this.rng = options.rng ?? Math.random;
    this.spawnTimer = this.drawSpawnInterval();
  }

  setSpawnEnabled(enabled: boolean): void {
    this.spawnEnabled = enabled;
  }

  setBounds(bounds: FairyBounds): void {
    assertBounds(bounds);
    this.bounds = bounds;

    for (const fairy of this.fairies) {
      fairy.x = clamp(fairy.x, bounds.minX, bounds.maxX);
      fairy.y = clamp(fairy.y, bounds.minY, bounds.maxY);
    }
  }

  update(dtSeconds: number): FairyTickResult {
    assertFinitePositive(dtSeconds, "dtSeconds");

    const popped: FairyId[] = [];

    for (const fairy of this.fairies) {
      this.integrateMotion(fairy, dtSeconds);

      fairy.vibrationPhase += dtSeconds * FAIRY_VIBRATION_FREQUENCY;

      if (fairy.isHovered) {
        fairy.hoverElapsed += dtSeconds;

        if (fairy.hoverElapsed >= FAIRY_HOVER_POP_SEC) {
          popped.push(fairy.id);
        }

        continue;
      }

      if (fairy.hoverElapsed > 0) {
        fairy.hoverElapsed = Math.max(0, fairy.hoverElapsed - dtSeconds);
      }
    }

    if (popped.length > 0) {
      const poppedSet = new Set(popped);
      this.fairies = this.fairies.filter((f) => !poppedSet.has(f.id));
    }

    if (this.spawnEnabled) {
      this.spawnTimer -= dtSeconds;

      while (this.spawnTimer <= 0) {
        if (this.fairies.length < FAIRY_MAX_ACTIVE) {
          this.fairies.push(this.createFairy());
        }

        this.spawnTimer += this.drawSpawnInterval();
      }
    }

    return { popped };
  }

  setHoveringFairy(id: FairyId | null): void {
    for (const fairy of this.fairies) {
      fairy.isHovered = fairy.id === id;
    }
  }

  getSnapshot(): FairySnapshot {
    return {
      fairies: this.fairies.map((f) => {
        const vibrationOffsetX =
          Math.sin(f.vibrationPhase) * FAIRY_VIBRATION_AMPLITUDE;
        const vibrationOffsetY =
          Math.cos(f.vibrationPhase * 0.83) * FAIRY_VIBRATION_AMPLITUDE;

        return {
          id: f.id,
          x: f.x,
          y: f.y,
          vibrationOffsetX,
          vibrationOffsetY,
          size: f.size,
          glowSize: f.size * FAIRY_GLOW_SCALE,
          color: {
            hue: f.hue,
            saturation: FAIRY_COLOR_SATURATION,
            value: FAIRY_COLOR_VALUE,
            alpha: FAIRY_COLOR_ALPHA,
          },
          glowAlpha: FAIRY_GLOW_ALPHA,
          hoverProgress: Math.min(1, f.hoverElapsed / FAIRY_HOVER_POP_SEC),
          isHovered: f.isHovered,
        };
      }),
    };
  }

  reset(): void {
    this.fairies = [];
    this.spawnTimer = this.drawSpawnInterval();
    this.nextFairyId = 1;
  }

  private createFairy(): FairyEntity {
    const id = `fairy-${this.nextFairyId}`;
    this.nextFairyId += 1;

    const size =
      FAIRY_SIZE_MIN + this.rng() * (FAIRY_SIZE_MAX - FAIRY_SIZE_MIN);

    const x = randomRange(this.rng, this.bounds.minX, this.bounds.maxX);
    const y = randomRange(this.rng, this.bounds.minY, this.bounds.maxY);

    const angle = this.rng() * Math.PI * 2;

    return {
      id,
      x,
      y,
      vx: Math.cos(angle) * FAIRY_MOVE_SPEED,
      vy: Math.sin(angle) * FAIRY_MOVE_SPEED,
      size,
      hue: this.rng(),
      vibrationPhase: this.rng() * Math.PI * 2,
      hoverElapsed: 0,
      isHovered: false,
    };
  }

  private integrateMotion(fairy: FairyEntity, dtSeconds: number): void {
    const { minX, maxX, minY, maxY } = this.bounds;

    fairy.x += fairy.vx * dtSeconds;
    fairy.y += fairy.vy * dtSeconds;

    if (fairy.x > maxX) {
      fairy.x = maxX;
      fairy.vx = -Math.abs(fairy.vx);
    } else if (fairy.x < minX) {
      fairy.x = minX;
      fairy.vx = Math.abs(fairy.vx);
    }

    if (fairy.y > maxY) {
      fairy.y = maxY;
      fairy.vy = -Math.abs(fairy.vy);
    } else if (fairy.y < minY) {
      fairy.y = minY;
      fairy.vy = Math.abs(fairy.vy);
    }
  }

  private drawSpawnInterval(): number {
    const range = FAIRY_SPAWN_MAX_SEC - FAIRY_SPAWN_MIN_SEC;
    return FAIRY_SPAWN_MIN_SEC + this.rng() * range;
  }
}

function assertFinitePositive(value: number, name: string): void {
  if (!Number.isFinite(value) || value <= 0) {
    throw new Error(`${name} must be a finite positive number`);
  }
}

function assertBounds(bounds: FairyBounds): void {
  if (
    !Number.isFinite(bounds.minX) ||
    !Number.isFinite(bounds.maxX) ||
    !Number.isFinite(bounds.minY) ||
    !Number.isFinite(bounds.maxY)
  ) {
    throw new Error("fairy bounds must be finite numbers");
  }
  if (bounds.minX >= bounds.maxX || bounds.minY >= bounds.maxY) {
    throw new Error("fairy bounds must satisfy min < max");
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function randomRange(rng: () => number, min: number, max: number): number {
  return min + rng() * (max - min);
}
